import React, { FC, ReactNode, useState } from 'react';

import { Button, Checkbox, IconButton } from '@rbilabs/universal-components';
import Clipboard from '@react-native-clipboard/clipboard';
import { StyleSheet } from 'react-native';

import { Box, Typography } from 'components/ui';

import theme from '../theme';

export interface IEnvItem {
  envKey: string;
  value?: string | null;
  infoMessage?: string;
}

export const EnvTitle = ({ children }: { children: ReactNode }) => (
  <Typography style={styles.title}>{children}</Typography>
);

export const EnvDescription = ({ children }: { children: ReactNode }) => {
  let value = children;

  try {
    if (typeof children === 'string') {
      value = JSON.parse(children);
    }
  } catch (_) {
    value = children;
  }

  return (
    <Box style={styles.description}>
      <Typography style={styles.descriptionText}>{JSON.stringify(value, null, 2)}</Typography>
    </Box>
  );
};

export const EnvItem = ({ envKey, value, infoMessage }: IEnvItem) => {
  if (typeof value === 'boolean') {
    value = value ? 'true' : 'false';
  }
  return (
    <Box style={styles.item}>
      <Box style={styles.row}>
        <EnvTitle>{envKey}</EnvTitle>
        <IconButton
          size="sm"
          iconName="info"
          onPress={() => {
            if (value) {
              Clipboard.setString(value);
              if (infoMessage) {
                alert(infoMessage);
              } else {
                alert(`Copied text "${value}"`);
              }
            }
          }}
        />
      </Box>
      <EnvDescription>{value ?? 'unknown'}</EnvDescription>
    </Box>
  );
};

interface IEnvItemBoolean {
  envKey: string;
  value: boolean;
}

export const EnvItemBoolean = ({ envKey, value }: IEnvItemBoolean) => (
  <Checkbox marginY="$4" name={envKey} value={envKey} isChecked={!!value}>
    {envKey}
  </Checkbox>
);

export const HiddenEnvItem = ({ envKey, value }: IEnvItem) => {
  const [visible, setVisible] = useState(false);
  if (typeof value === 'boolean') {
    value = value ? 'true' : 'false';
  }

  return (
    <Box style={styles.hiddenItem}>
      <Box style={[styles.row, styles.spaceBetween]}>
        <EnvTitle>{envKey}</EnvTitle>
        <Button onPress={() => setVisible(v => !v)} size={'md'} variant="ghost" mb="$1">
          {visible ? 'Hide Value' : 'Show Value'}
        </Button>
      </Box>
      {visible ? <EnvDescription>{value ?? 'unknown'}</EnvDescription> : null}
    </Box>
  );
};

export const ServiceModeItem: FC<{
  envKey: string;
  value: Record<string, unknown>;
}> = ({ envKey, value }) => (
  <Box>
    <Typography variant="headerFour" mb="$1">
      {envKey}
    </Typography>
    <Box style={styles.serviceModeDetails}>
      {Object.keys(value).map(statusKey => (
        <Typography key={statusKey}>{`${statusKey}: ${Boolean(value[statusKey])}`}</Typography>
      ))}
    </Box>
  </Box>
);

const styles = StyleSheet.create({
  description: {
    backgroundColor: 'white',
    borderRadius: 4,
    marginTop: 8,
    overflow: 'scroll',
    padding: 8,
  },
  descriptionText: {
    fontFamily: 'monospace',
    fontSize: 12,
    fontWeight: 'normal',
    lineHeight: 14,
    textAlign: 'left',
  },
  hiddenItem: { paddingBottom: 4, width: '100%' },
  item: { paddingBottom: 8, width: '100%' },
  row: { alignItems: 'center', flexDirection: 'row' },
  serviceModeDetails: { backgroundColor: 'white', borderRadius: 4, marginBottom: 16, padding: 8 },
  spaceBetween: { justifyContent: 'space-between' },
  title: { color: theme.envTitleColor, flex: 1 },
});
